/**
 * FieldScoutDesktopCapture — the active recording view for the desktop
 * paper-transcription mode. Composes useFieldScoutDesktop + MatrixMode +
 * PointMode + OrchardTalliesBar into the dual-mode layout.
 *
 * Track: field_scout_recording_desktop / Slice 3 / Task 10.
 */
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type Pest } from '../../../../db';
import { normalizeMeasurementType, typeDefaultThreshold } from '../../../../scouting/pestConfig';
import { flagFor } from '../../../../scouting/recording';
import { useFieldScoutDesktop } from './useFieldScoutDesktop';
import { MatrixMode, type MatrixPest } from './MatrixMode';
import { PointMode, type PointModePest } from './PointMode';
import { OrchardTalliesBar, type TalliesPest } from './OrchardTalliesBar';
import { FieldScoutReview, type ReviewRow } from '../FieldScoutReview';
import './FieldScoutDesktop.css';

// ─────────────────────────────────────────────────────────────────────────────
//  Props
// ─────────────────────────────────────────────────────────────────────────────

export interface FieldScoutDesktopCaptureProps {
  orchardId: string;
  orchardName: string;
  scouterName: string;
  pests: Pest[];
  onSubmitDone: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────────────────────

function getPestThreshold(pest: Pest): number {
  return typeof pest.actionThreshold === 'number'
    ? pest.actionThreshold
    : typeDefaultThreshold(normalizeMeasurementType(pest.measurementType));
}

// ─────────────────────────────────────────────────────────────────────────────
//  Component
// ─────────────────────────────────────────────────────────────────────────────

export const FieldScoutDesktopCapture: React.FC<FieldScoutDesktopCaptureProps> = ({
  orchardId,
  orchardName,
  scouterName,
  pests,
  onSubmitDone,
}) => {
  const { t } = useTranslation();

  const rec = useFieldScoutDesktop(orchardId, orchardName, scouterName, pests);

  // ── Derived pest lists ────────────────────────────────────────────────────

  const incidencePests = pests.filter(
    (p) => normalizeMeasurementType(p.measurementType) === 'incidence',
  );

  const levelPests = pests.filter(
    (p) => normalizeMeasurementType(p.measurementType) === 'level',
  );

  const countPests = pests.filter(
    (p) => normalizeMeasurementType(p.measurementType) === 'count',
  );

  // Shape for MatrixMode / PointMode
  const incPestsForMatrix: MatrixPest[] = incidencePests.map((p) => ({
    id: p.id,
    code: p.code,
    name: p.name,
    actionThreshold: getPestThreshold(p),
  }));

  const incPestsForPoint: PointModePest[] = incidencePests.map((p) => ({
    id: p.id,
    code: p.code,
    name: p.name,
    actionThreshold: getPestThreshold(p),
  }));

  // Shape for OrchardTalliesBar
  const levelTalliesPests: TalliesPest[] = levelPests.map((p) => ({
    id: p.id,
    code: p.code,
    name: p.name,
    measurementType: p.measurementType ?? 'level',
    actionThreshold: getPestThreshold(p),
  }));

  const countTalliesPests: TalliesPest[] = countPests.map((p) => ({
    id: p.id,
    code: p.code,
    name: p.name,
    measurementType: p.measurementType ?? 'count',
    actionThreshold: getPestThreshold(p),
  }));

  // ── isOver helper ────────────────────────────────────────────────────────

  const isOver = (pestId: string): boolean => {
    const pest = pests.find((p) => p.id === pestId);
    const threshold = pest ? getPestThreshold(pest) : typeDefaultThreshold('incidence');
    return flagFor(rec.pctFor(pestId), threshold) === 'exceeded';
  };

  // ── Photos set for PointMode ──────────────────────────────────────────────

  const photosSet = useMemo(
    () => new Set(Object.keys(rec.photos).filter(k => (rec.photos as any)[+k]?.length).map(Number)),
    [rec.photos],
  );

  // ── Submit handler ────────────────────────────────────────────────────────

  const [submitting, setSubmitting] = useState(false);
  const [reviewing, setReviewing] = useState(false);

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await rec.submit();
      onSubmitDone();
    } catch (err) {
      console.error('[FieldScoutDesktopCapture] submit error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Per-session review rows (mirrors mobile FieldScoutModePage) ────────────
  const reviewRows: ReviewRow[] = [];
  for (const p of pests) {
    const mt = normalizeMeasurementType(p.measurementType);
    const threshold = getPestThreshold(p);
    if (mt === 'incidence') {
      const value = rec.pctFor(p.id);
      if (value > 0) reviewRows.push({ code: p.code, name: p.name, type: 'incidence', value, threshold, display: `${value}%` });
    } else if (mt === 'level') {
      const value = rec.levels[p.id] ?? 0;
      if (value > 0) reviewRows.push({ code: p.code, name: p.name, type: 'level', value, threshold, display: `${value}/4` });
    } else {
      const value = rec.counts[p.id] ?? 0;
      if (value > 0) reviewRows.push({ code: p.code, name: p.name, type: 'count', value, threshold, display: `${value}` });
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  // Static chrome — always shown
  const chrome = (
    <>
      {/* Breadcrumb */}
      <div className="fsd-breadcrumb">
        <span>{t('nav.scouting')}</span>
        <span className="fsd-breadcrumb__sep">/</span>
        <span>{orchardName}</span>
        <span className="fsd-breadcrumb__sep">/</span>
        <span>{t('fieldScout.desktop.start', 'Manual capture')}</span>
      </div>

      {/* Page header */}
      <div className="fsd-header">
        <h1 className="fsd-header__title">
          {t('fieldScout.desktop.title', { block: orchardName })}
        </h1>
        <span className="fsd-header__tag">
          {t('fieldScout.desktop.paperTag')}
        </span>
      </div>
    </>
  );

  // Loading state — show after start is clicked but draft not yet hydrated
  if (rec.loading) {
    return (
      <div className="fsd-page">
        {chrome}
        <div className="fsd-loading" data-testid="fsd-loading">
          {t('fieldScout.recording.start')}…
        </div>
      </div>
    );
  }

  // Review state — per-session summary before submit (mirrors mobile)
  if (reviewing) {
    return (
      <div className="fsd-page" data-testid="desk-review">
        {chrome}
        <button
          type="button"
          className="btn btn-link btn-sm px-0"
          data-testid="desk-review-back"
          onClick={() => setReviewing(false)}
        >
          ← {t('fieldScout.desktop.backToCapture')}
        </button>
        <FieldScoutReview
          blockName={orchardName}
          examined={rec.examined}
          date={rec.dateWalked}
          rows={reviewRows}
          onSubmit={handleSubmit}
          submitting={submitting}
        />
      </div>
    );
  }

  // Ready state — draft hydrated
  return (
    <div className="fsd-page">
      {chrome}

      {/* Mode toggle */}
      <div className="fsd-mode-row">
        <span className="fsd-mode-row__label">{t('fieldScout.desktop.mode.label')}</span>
        <button
          type="button"
          data-testid="desk-mode-matrix"
          className={`fsd-mode-btn${rec.mode === 'matrix' ? ' fsd-mode-btn--active' : ''}`}
          onClick={() => rec.setMode('matrix')}
        >
          {t('fieldScout.desktop.mode.matrix')}
        </button>
        <button
          type="button"
          data-testid="desk-mode-point"
          className={`fsd-mode-btn${rec.mode === 'point' ? ' fsd-mode-btn--active' : ''}`}
          onClick={() => rec.setMode('point')}
        >
          {t('fieldScout.desktop.mode.point')}
        </button>
      </div>

      {/* Action buttons */}
      <div className="fsd-actions">
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={() => { rec.saveDraft().catch(e => console.error('[FieldScoutDesktopCapture] saveDraft error:', e)); }}
        >
          {t('fieldScout.desktop.saveDraft')}
        </button>
        <button
          type="button"
          className="btn btn-success btn-sm"
          onClick={() => setReviewing(true)}
          disabled={submitting}
        >
          {t('fieldScout.desktop.finishReview')}
        </button>
      </div>

      {/* Metadata toolbar */}
      <div className="fsd-meta">
        <div className="fsd-meta__item">
          <span className="fsd-meta__lbl">{t('fieldScout.desktop.scouterOnSheet')}</span>
          <span className="fsd-meta__val">{scouterName}</span>
        </div>
        <div className="fsd-meta__item">
          <span className="fsd-meta__lbl">{t('fieldScout.desktop.dateWalked')}</span>
          <input
            type="date"
            className="fsd-meta__input"
            value={rec.dateWalked}
            onChange={(e) => rec.setDateWalked(e.target.value)}
          />
        </div>
        <div className="fsd-meta__item">
          <span className="fsd-meta__lbl">{t('fieldScout.desktop.samplingUnit')}</span>
          <span className="fsd-meta__val">{t('fieldScout.desktop.samplingUnitValue')}</span>
        </div>
        <div className="fsd-meta__item">
          <span className="fsd-meta__lbl">{t('fieldScout.desktop.pointsFilled')}</span>
          <span className="fsd-meta__val">{rec.examined} / 100</span>
        </div>
      </div>

      {/* Orchard-wide tallies bar */}
      <div data-testid="desk-tallies-bar">
        <OrchardTalliesBar
          levelPests={levelTalliesPests}
          countPests={countTalliesPests}
          levels={rec.levels}
          counts={rec.counts}
          onRaise={rec.raiseLevel}
          onLower={rec.lowerLevel}
          onInc={rec.incCount}
          onDec={rec.decCount}
        />
      </div>

      {/* Active mode pane */}
      {rec.mode === 'matrix' ? (
        <MatrixMode
          incPests={incPestsForMatrix}
          cellState={rec.cellState}
          pctFor={rec.pctFor}
          treeInfestedCount={rec.treeInfestedCount}
          isOver={isOver}
          onCycle={rec.cycleCell}
          onMarkClean={rec.markPointClean}
        />
      ) : (
        <PointMode
          incPests={incPestsForPoint}
          activePestId={rec.activePestId}
          gridMode={rec.gridMode}
          currentPoint={rec.currentPoint}
          cellState={rec.cellState}
          pctFor={rec.pctFor}
          isOver={isOver}
          photos={photosSet}
          allInfestedAt={(point: number) => incidencePests.some(p => p.id !== rec.activePestId && rec.cellState(point, p.id) === 'infested')}
          onSelectPest={rec.setActivePest}
          onSetGridMode={rec.setGridMode}
          onPickPoint={rec.setCurrentPoint}
          onSetVerdict={rec.setVerdict}
          onMarkClean={rec.markPointClean}
          onPrev={() => rec.setCurrentPoint(Math.max(0, rec.currentPoint - 1))}
          onNext={() => rec.setCurrentPoint(Math.min(99, rec.currentPoint + 1))}
          onNextEmpty={rec.nextEmpty}
          onAttachPhoto={rec.capturePhoto}
        />
      )}
    </div>
  );
};

export default FieldScoutDesktopCapture;
